import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from './LanguageContext';
import { fetchWithAuth, API_URL } from '../utils/api';
import { formatDate, calculateAge } from '../utils/AuthUtils';
import PatientRecord from './PatientRecord';
import Pagination from './Pagination';
import useIsMobile from '../hooks/useIsMobile';
import { Users, Plus, Search, Edit, Eye, Trash2, X, Save, Upload, User, CreditCard, AlertTriangle, FileText } from 'lucide-react';

const Patients = ({ currentBranch, user }) => {
  const { print_lang } = useLanguage();
  const isMobile = useIsMobile();
  const [patients, setPatients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add', 'edit', 'view'
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [formData, setFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Photo upload states
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [removePhoto, setRemovePhoto] = useState(false);
  const photoInputRef = useRef(null);

  // Smartcard reading state
  const [isReadingCard, setIsReadingCard] = useState(false);

  // Validation states
  const [citizenIdError, setCitizenIdError] = useState(null);
  const [citizenIdWarning, setCitizenIdWarning] = useState(null);
  const [isCheckingCitizenId, setIsCheckingCitizenId] = useState(false);
  const [expiredCardWarning, setExpiredCardWarning] = useState(null);

  // Clinical record view state
  const [showClinicalRecord, setShowClinicalRecord] = useState(false);
  const [clinicalRecordPatient, setClinicalRecordPatient] = useState(null);

  useEffect(() => {
    if (currentBranch?.branch_id) {
      fetchPatients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBranch, currentPage, pageSize]);

  const fetchPatients = async (resetPage = false) => {
    setIsLoading(true);
    try {
      const page = resetPage ? 1 : currentPage;
      if (resetPage) setCurrentPage(1);

      const params = new URLSearchParams({
        branch_id: currentBranch.branch_id,
        page: page,
        limit: pageSize,
        ...(searchTerm && { search: searchTerm })
      });
      const data = await fetchWithAuth(`/api/patients?${params}`);
      setPatients(data.data || []);

      // Set pagination info from response
      if (data.pagination) {
        setTotalCount(data.pagination.totalCount || 0);
        setTotalPages(data.pagination.totalPages || 0);
      }
    } catch (error) {
      console.error('Failed to fetch patients:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API_URL}/${path}`;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPatients(true); // Reset to page 1 on new search
  };

  const getInitialFormData = () => ({
    dn: '',
    citizen_id: '',
    prefix: '',
    name: '',
    name_en: '',
    gender: 'other',
    dob: '',
    blood_group: '',
    phone: '',
    email: '',
    address: '',
    medical_history: '',
    drug_allergy: '',
    emergency_contact: '',
    emergency_phone: '',
    status: 'active'
  });

  const resetPhotoStates = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setRemovePhoto(false);
  };

  const resetValidationStates = () => {
    setCitizenIdError(null);
    setCitizenIdWarning(null);
    setExpiredCardWarning(null);
  };

  const handleAdd = () => {
    setSelectedPatient(null);
    setFormData(getInitialFormData());
    resetPhotoStates();
    resetValidationStates();
    setModalMode('add');
    setShowModal(true);
  };

  const handleView = (patient) => {
    setSelectedPatient(patient);
    resetPhotoStates();
    resetValidationStates();
    setModalMode('view');
    setShowModal(true);
  };

  const handleEdit = (patient) => {
    setSelectedPatient(patient);
    setFormData({
      dn: patient.dn || '',
      citizen_id: patient.citizen_id || '',
      prefix: patient.prefix || '',
      name: patient.name || '',
      name_en: patient.name_en || '',
      gender: patient.gender || 'other',
      dob: patient.dob ? patient.dob.split('T')[0] : '',
      blood_group: patient.blood_group || '',
      phone: patient.phone || '',
      email: patient.email || '',
      address: patient.address || '',
      medical_history: patient.medical_history || '',
      drug_allergy: patient.drug_allergy || '',
      emergency_contact: patient.emergency_contact || '',
      emergency_phone: patient.emergency_phone || '',
      status: patient.status || 'active'
    });
    resetPhotoStates();
    resetValidationStates();
    if (patient.images_path) {
      setPhotoPreview(getImageUrl(patient.images_path));
    }
    setModalMode('edit');
    setShowModal(true);
  };

  const handleSwitchToEdit = () => {
    if (selectedPatient) {
      setFormData({
        dn: selectedPatient.dn || '',
        citizen_id: selectedPatient.citizen_id || '',
        prefix: selectedPatient.prefix || '',
        name: selectedPatient.name || '',
        name_en: selectedPatient.name_en || '',
        gender: selectedPatient.gender || 'other',
        dob: selectedPatient.dob ? selectedPatient.dob.split('T')[0] : '',
        blood_group: selectedPatient.blood_group || '',
        phone: selectedPatient.phone || '',
        email: selectedPatient.email || '',
        address: selectedPatient.address || '',
        medical_history: selectedPatient.medical_history || '',
        drug_allergy: selectedPatient.drug_allergy || '',
        emergency_contact: selectedPatient.emergency_contact || '',
        emergency_phone: selectedPatient.emergency_phone || '',
        status: selectedPatient.status || 'active'
      });
      resetPhotoStates();
      if (selectedPatient.images_path) {
        setPhotoPreview(getImageUrl(selectedPatient.images_path));
      }
      setModalMode('edit');
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
      setRemovePhoto(false);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setRemovePhoto(true);
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  // Convert base64 to File object
  const base64ToFile = (base64String, filename) => {
    // Remove data URL prefix if present
    const base64Data = base64String.replace(/^data:image\/\w+;base64,/, '');
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'image/jpeg' });
    return new File([blob], filename, { type: 'image/jpeg' });
  };

  // Extract prefix from Thai name
  const extractPrefixAndName = (fullName) => {
    const prefixes = ['นาย', 'นาง', 'นางสาว', 'เด็กชาย', 'เด็กหญิง', 'ด.ช.', 'ด.ญ.'];
    for (const prefix of prefixes) {
      if (fullName.startsWith(prefix)) {
        return {
          prefix: prefix === 'ด.ช.' ? 'เด็กชาย' : prefix === 'ด.ญ.' ? 'เด็กหญิง' : prefix,
          name: fullName.substring(prefix.length).trim()
        };
      }
    }
    return { prefix: '', name: fullName };
  };

  // Convert Buddhist year date to Christian year
  const convertBuddhistDate = (dateStr) => {
    if (!dateStr) return '';

    let year, month, day;

    // Remove any spaces or dashes
    const cleanDate = dateStr.toString().replace(/[\s-]/g, '');

    if (cleanDate.includes('/')) {
      // Format: DD/MM/YYYY (Buddhist year)
      const parts = cleanDate.split('/');
      day = parseInt(parts[0]);
      month = parseInt(parts[1]);
      year = parseInt(parts[2]) - 543;
    } else if (cleanDate.length === 8) {
      // Format: YYYYMMDD (Buddhist year)
      year = parseInt(cleanDate.substring(0, 4)) - 543;
      month = parseInt(cleanDate.substring(4, 6));
      day = parseInt(cleanDate.substring(6, 8));
    } else if (cleanDate.length === 10 && cleanDate.includes('-')) {
      // Format: YYYY-MM-DD (Buddhist year)
      const parts = dateStr.split('-');
      year = parseInt(parts[0]) - 543;
      month = parseInt(parts[1]);
      day = parseInt(parts[2]);
    } else {
      console.log('Unknown date format:', dateStr);
      return '';
    }

    // Validate the date
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      console.log('Invalid date values:', { year, month, day });
      return '';
    }

    // Format as YYYY-MM-DD for input[type="date"]
    const formattedDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    console.log('Converted date:', dateStr, '->', formattedDate);
    return formattedDate;
  };

  // Check if ID card is expired
  const isCardExpired = (expireDateStr) => {
    if (!expireDateStr) return false;
    const expireDate = convertBuddhistDate(expireDateStr);
    if (!expireDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expire = new Date(expireDate);
    return expire < today;
  };

  // Validate citizen ID format (Thai 13-digit)
  const isValidCitizenId = (citizenId) => {
    if (!citizenId) return true; // Optional field
    if (citizenId.length !== 13) return false;
    if (!/^\d{13}$/.test(citizenId)) return false;

    // Thai citizen ID checksum validation
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      sum += parseInt(citizenId[i]) * (13 - i);
    }
    const checkDigit = (11 - (sum % 11)) % 10;
    return parseInt(citizenId[12]) === checkDigit;
  };

  // Check for duplicate citizen ID
  const checkDuplicateCitizenId = async (citizenId, excludePatientId = null) => {
    if (!citizenId || citizenId.length !== 13) {
      setCitizenIdError(null);
      setCitizenIdWarning(null);
      return true;
    }

    setIsCheckingCitizenId(true);
    try {
      const result = await fetchWithAuth(`/api/patients/check-citizen/${citizenId}`);

      if (result.exists) {
        // If editing, check if it's the same patient
        if (excludePatientId && result.patient?.patient_id === excludePatientId) {
          setCitizenIdError(null);
          setCitizenIdWarning(null);
          return true;
        }

        setCitizenIdError({
          message: print_lang('citizen_id_exists', 'This citizen ID is already registered'),
          patient: result.patient
        });
        return false;
      } else {
        setCitizenIdError(null);
        return true;
      }
    } catch (error) {
      console.error('Failed to check citizen ID:', error);
      return true; // Allow submission on error
    } finally {
      setIsCheckingCitizenId(false);
    }
  };

  // Handle citizen ID blur for validation
  const handleCitizenIdBlur = async () => {
    const citizenId = formData.citizen_id;

    // Validate format first
    if (citizenId && citizenId.length === 13 && !isValidCitizenId(citizenId)) {
      setCitizenIdWarning(print_lang('invalid_citizen_id_format', 'Invalid citizen ID format'));
      return;
    } else {
      setCitizenIdWarning(null);
    }

    // Check for duplicates
    const excludeId = modalMode === 'edit' ? selectedPatient?.patient_id : null;
    await checkDuplicateCitizenId(citizenId, excludeId);
  };

  // Read smartcard data
  const handleReadSmartcard = async () => {
    setIsReadingCard(true);
    setExpiredCardWarning(null);
    setCitizenIdError(null);

    try {
      const response = await fetch('http://localhost:8081/json');
      if (!response.ok) {
        throw new Error('Failed to read smartcard');
      }

      const data = await response.json();
      console.log('Smartcard data:', data);
      console.log('DoB from smartcard:', data.DoB);
      console.log('ExpireDate from smartcard:', data.ExpireDate);

      // Check if card is expired
      if (data.ExpireDate && isCardExpired(data.ExpireDate)) {
        const expireDate = convertBuddhistDate(data.ExpireDate);
        setExpiredCardWarning({
          message: print_lang('card_expired', 'This ID card has expired'),
          expireDate: expireDate
        });
      }

      // Extract prefix and name from NameTH
      const { prefix, name } = extractPrefixAndName(data.NameTH || '');

      // Map gender
      let gender = 'other';
      if (data.Sex === 'ชาย') gender = 'male';
      else if (data.Sex === 'หญิง') gender = 'female';

      // Convert date of birth
      const dob = convertBuddhistDate(data.DoB);

      // Update form data
      setFormData(prev => ({
        ...prev,
        citizen_id: data.CitizenID || '',
        prefix: prefix,
        name: name,
        gender: gender,
        dob: dob,
        address: (data.Address || '').trim()
      }));

      // Handle photo
      if (data.Photo) {
        const photoFile = base64ToFile(data.Photo, `smartcard-${data.CitizenID || Date.now()}.jpg`);
        setPhotoFile(photoFile);
        setPhotoPreview(URL.createObjectURL(photoFile));
        setRemovePhoto(false);
      }

      // Check for duplicate citizen ID
      if (data.CitizenID) {
        const excludeId = modalMode === 'edit' ? selectedPatient?.patient_id : null;
        await checkDuplicateCitizenId(data.CitizenID, excludeId);
      }

    } catch (error) {
      console.error('Failed to read smartcard:', error);
      alert(print_lang('smartcard_error', 'Failed to read smartcard. Please make sure the card reader is connected and the card is inserted.'));
    } finally {
      setIsReadingCard(false);
    }
  };

  // The API's DN rejections carry a stable `code`; the `message` beside it is
  // English only, and this app is bilingual. Translate on the code and fall
  // back to the API's own wording for anything unrecognised.
  const dnErrorMessage = (result) => {
    switch (result && result.code) {
      case 'DN_TAKEN':
        return print_lang('dn_taken', 'This dental number is already used in this branch');
      case 'DN_IMMUTABLE':
        return print_lang('dn_immutable', 'A dental number cannot be changed once assigned');
      case 'DN_INVALID':
        return print_lang('dn_invalid', 'Dental number must look like DN-69-00001');
      default:
        return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate citizen ID format
    if (formData.citizen_id && formData.citizen_id.length === 13 && !isValidCitizenId(formData.citizen_id)) {
      setCitizenIdWarning(print_lang('invalid_citizen_id_format', 'Invalid citizen ID format'));
      return;
    }

    // Check for duplicate citizen ID before submission
    if (formData.citizen_id) {
      const excludeId = modalMode === 'edit' ? selectedPatient?.patient_id : null;
      const isUnique = await checkDuplicateCitizenId(formData.citizen_id, excludeId);
      if (!isUnique) {
        return; // Error is already set by checkDuplicateCitizenId
      }
    }

    setIsSaving(true);

    try {
      const formDataToSend = new FormData();

      // Append all form fields
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined) {
          formDataToSend.append(key, formData[key]);
        }
      });

      formDataToSend.append('branch_id', currentBranch.branch_id);

      // Append photo if selected
      if (photoFile) {
        formDataToSend.append('photo', photoFile);
      }

      // Append removal flag
      if (removePhoto) {
        formDataToSend.append('remove_photo', 'true');
      }

      const token = localStorage.getItem('token');
      const url = selectedPatient
        ? `${API_URL}/api/patients/${selectedPatient.patient_id}`
        : `${API_URL}/api/patients`;

      const response = await fetch(url, {
        method: selectedPatient ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(dnErrorMessage(result) || result.message || 'Failed to save patient');
      }

      setShowModal(false);
      fetchPatients();
    } catch (error) {
      console.error('Failed to save patient:', error);
      alert(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (patientId) => {
    if (!window.confirm(print_lang('confirm_delete', 'Are you sure you want to delete?'))) return;

    try {
      await fetchWithAuth(`/api/patients/${patientId}`, { method: 'DELETE' });
      fetchPatients();
    } catch (error) {
      console.error('Failed to delete patient:', error);
      alert(error.message);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPatient(null);
    setFormData({});
    resetPhotoStates();
    resetValidationStates();
  };

  const handleViewClinicalRecord = (patient) => {
    setClinicalRecordPatient(patient);
    setShowClinicalRecord(true);
  };

  const handleCloseClinicalRecord = () => {
    setShowClinicalRecord(false);
    setClinicalRecordPatient(null);
  };

  const prefixOptions = [
    { value: '', label: '--' },
    { value: 'นาย', label: 'นาย' },
    { value: 'นาง', label: 'นาง' },
    { value: 'นางสาว', label: 'นางสาว' },
    { value: 'เด็กชาย', label: 'เด็กชาย' },
    { value: 'เด็กหญิง', label: 'เด็กหญิง' },
    { value: 'Mr.', label: 'Mr.' },
    { value: 'Mrs.', label: 'Mrs.' },
    { value: 'Ms.', label: 'Ms.' },
    { value: 'Miss', label: 'Miss' }
  ];

  const bloodGroupOptions = [
    { value: '', label: '--' },
    { value: 'A', label: 'A' },
    { value: 'B', label: 'B' },
    { value: 'AB', label: 'AB' },
    { value: 'O', label: 'O' },
    { value: 'A+', label: 'A+' },
    { value: 'A-', label: 'A-' },
    { value: 'B+', label: 'B+' },
    { value: 'B-', label: 'B-' },
    { value: 'AB+', label: 'AB+' },
    { value: 'AB-', label: 'AB-' },
    { value: 'O+', label: 'O+' },
    { value: 'O-', label: 'O-' }
  ];

  // Row pieces shared by the table branch (desktop) and the card branch
  // (phone, <768px). Kept as single functions so the two layouts can never
  // drift apart - see Task 8 brief.
  const patientAvatar = (patient) => (
    patient.images_path ? (
      <img
        src={getImageUrl(patient.images_path)}
        alt={patient.name}
        className="w-10 h-10 rounded-full object-cover"
      />
    ) : (
      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
        <User size={20} className="text-gray-400" />
      </div>
    )
  );

  const genderBadge = (patient) => (
    <span className={`badge ${patient.gender === 'male' ? 'badge-info' : patient.gender === 'female' ? 'badge-warning' : 'badge-secondary'}`}>
      {print_lang(patient.gender, patient.gender)}
    </span>
  );

  const statusBadge = (patient) => (
    <span className={`badge ${patient.status === 'active' ? 'badge-success' : 'badge-error'}`}>
      {print_lang(patient.status, patient.status === 'active' ? 'Active' : 'Inactive')}
    </span>
  );

  const patientActions = (patient) => (
    <div className="flex gap-1 justify-end">
      <button
        onClick={() => handleViewClinicalRecord(patient)}
        className="p-2 hover:bg-green-50 rounded"
        title={print_lang('view_clinical_record', 'View Clinical Record')}
      >
        <FileText size={16} className="text-green-600" />
      </button>
      <button
        onClick={() => handleView(patient)}
        className="p-2 hover:bg-gray-100 rounded"
        title={print_lang('view', 'View')}
      >
        <Eye size={16} className="text-gray-600" />
      </button>
      <button
        onClick={() => handleEdit(patient)}
        className="p-2 hover:bg-gray-100 rounded"
        title={print_lang('edit', 'Edit')}
      >
        <Edit size={16} className="text-blue-600" />
      </button>
      <button
        onClick={() => handleDelete(patient.patient_id)}
        className="p-2 hover:bg-gray-100 rounded"
        title={print_lang('delete', 'Delete')}
      >
        <Trash2 size={16} className="text-red-600" />
      </button>
    </div>
  );

  // If clinical record view is active, show PatientRecord component
  if (showClinicalRecord && clinicalRecordPatient) {
    return (
      <PatientRecord
        patient={clinicalRecordPatient}
        currentBranch={currentBranch}
        user={user}
        onClose={handleCloseClinicalRecord}
        onPatientUpdate={fetchPatients}
      />
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Users size={28} />
          {print_lang('patients', 'Patients')}
        </h1>

        <button onClick={handleAdd} className="btn btn-primary flex items-center gap-2">
          <Plus size={18} />
          {print_lang('add', 'Add')} {print_lang('patient', 'Patient')}
        </button>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="card mb-6">
        <div className="flex gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={print_lang('search_patients_dn', 'Search by DN, name, phone or citizen ID...')}
                className="pl-10"
              />
            </div>
          </div>
          <button type="submit" className="btn btn-primary">
            {print_lang('search', 'Search')}
          </button>
        </div>
      </form>

      {/* Table (desktop) / Card list (phone, <768px) */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : isMobile ? (
          <ul className="patient-card-list">
            {patients.length === 0 ? (
              <li className="patient-card-empty text-center text-gray-500 py-8">
                {print_lang('no_data', 'No data')}
              </li>
            ) : (
              patients.map((patient) => (
                <li className="patient-card" key={patient.patient_id}>
                  <div className="patient-card-top">
                    <div className="patient-card-avatar">{patientAvatar(patient)}</div>
                    <div className="patient-card-identity">
                      <strong className="patient-card-name">{patient.prefix} {patient.name}</strong>
                      {patient.name_en && (
                        <span className="patient-card-name-en">{patient.name_en}</span>
                      )}
                      <span className="patient-card-dn font-mono">{patient.dn || '-'}</span>
                    </div>
                    {statusBadge(patient)}
                  </div>
                  <dl className="patient-card-fields">
                    <div className="patient-card-field">
                      <dt>{print_lang('citizen_id', 'Citizen ID')}</dt>
                      <dd className="font-mono">{patient.citizen_id || '-'}</dd>
                    </div>
                    <div className="patient-card-field">
                      <dt>{print_lang('phone', 'Phone')}</dt>
                      <dd>{patient.phone || '-'}</dd>
                    </div>
                    <div className="patient-card-field">
                      <dt>{print_lang('age', 'Age')}</dt>
                      <dd>{patient.dob ? calculateAge(patient.dob) : '-'}</dd>
                    </div>
                    <div className="patient-card-field">
                      <dt>{print_lang('gender', 'Gender')}</dt>
                      <dd>{genderBadge(patient)}</dd>
                    </div>
                    <div className="patient-card-field">
                      <dt>{print_lang('last_visit', 'Last Visit')}</dt>
                      <dd>{patient.last_visit ? formatDate(patient.last_visit) : '-'}</dd>
                    </div>
                  </dl>
                  <div className="patient-card-actions">
                    {patientActions(patient)}
                  </div>
                </li>
              ))
            )}
          </ul>
        ) : (
          <table>
            <thead>
              <tr>
                <th></th>
                <th>{print_lang('dn', 'DN')}</th>
                <th>{print_lang('name', 'Name')}</th>
                <th>{print_lang('citizen_id', 'Citizen ID')}</th>
                <th>{print_lang('phone', 'Phone')}</th>
                <th>{print_lang('age', 'Age')}</th>
                <th>{print_lang('gender', 'Gender')}</th>
                <th>{print_lang('last_visit', 'Last Visit')}</th>
                <th>{print_lang('status', 'Status')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {patients.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center text-gray-500 py-8">
                    {print_lang('no_data', 'No data')}
                  </td>
                </tr>
              ) : (
                patients.map((patient) => (
                  <tr key={patient.patient_id}>
                    <td className="w-12">
                      {patientAvatar(patient)}
                    </td>
                    <td className="font-mono text-sm whitespace-nowrap">{patient.dn || '-'}</td>
                    <td>
                      <div>
                        <p className="font-medium">{patient.prefix} {patient.name}</p>
                        {patient.name_en && (
                          <p className="text-sm text-gray-500">{patient.name_en}</p>
                        )}
                      </div>
                    </td>
                    <td className="font-mono text-sm">{patient.citizen_id || '-'}</td>
                    <td>{patient.phone || '-'}</td>
                    <td>{patient.dob ? calculateAge(patient.dob) : '-'}</td>
                    <td>{genderBadge(patient)}</td>
                    <td>{patient.last_visit ? formatDate(patient.last_visit) : '-'}</td>
                    <td>{statusBadge(patient)}</td>
                    <td>{patientActions(patient)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {!isLoading && patients.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            print_lang={print_lang}
          />
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">
                {modalMode === 'view'
                  ? print_lang('patient_details', 'Patient Details')
                  : modalMode === 'edit'
                    ? print_lang('edit', 'Edit') + ' ' + print_lang('patient', 'Patient')
                    : print_lang('add', 'Add') + ' ' + print_lang('patient', 'Patient')
                }
              </h2>
              <button onClick={closeModal}>
                <X size={24} />
              </button>
            </div>

            {modalMode === 'view' && selectedPatient ? (
              /* View Mode */
              <div className="space-y-6">
                {/* Photo Display */}
                <div className="flex justify-center">
                  {selectedPatient.images_path ? (
                    <img
                      src={getImageUrl(selectedPatient.images_path)}
                      alt={selectedPatient.name}
                      className="w-32 h-32 rounded-full object-cover border-4 border-gray-200"
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-full bg-gray-100 flex items-center justify-center border-4 border-gray-200">
                      <User size={48} className="text-gray-300" />
                    </div>
                  )}
                </div>

                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('prefix', 'Prefix')}</label>
                    <p>{selectedPatient.prefix || '-'}</p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('name', 'Name')}</label>
                    <p className="font-medium">{selectedPatient.name}</p>
                  </div>
                </div>

                {/* grid-cols-1 below 768px: Citizen ID is 13 unbroken digits with
                    no natural line-break opportunity, so a 3-up row at 390px
                    (~103px per column) can push it past its column - the same
                    field gets a full 2-up card column (~171px) in the phone
                    list above and never needs this. */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('name_en', 'Name (English)')}</label>
                    <p>{selectedPatient.name_en || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('dn', 'DN')}</label>
                    <p className="font-mono">{selectedPatient.dn || print_lang('dn_not_assigned', 'Not assigned')}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('citizen_id', 'Citizen ID')}</label>
                    <p className="font-mono">{selectedPatient.citizen_id || '-'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('gender', 'Gender')}</label>
                    <span className={`badge ${selectedPatient.gender === 'male' ? 'badge-info' : selectedPatient.gender === 'female' ? 'badge-warning' : 'badge-secondary'}`}>
                      {print_lang(selectedPatient.gender, selectedPatient.gender)}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('dob', 'Date of Birth')}</label>
                    <p>{selectedPatient.dob ? formatDate(selectedPatient.dob) : '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('age', 'Age')}</label>
                    <p>{selectedPatient.dob ? calculateAge(selectedPatient.dob) : '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('blood_group', 'Blood Group')}</label>
                    <p className="font-medium text-red-600">{selectedPatient.blood_group || '-'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('phone', 'Phone')}</label>
                    <p>{selectedPatient.phone || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('email', 'Email')}</label>
                    <p>{selectedPatient.email || '-'}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-500 mb-1">{print_lang('address', 'Address')}</label>
                  <p>{selectedPatient.address || '-'}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('medical_history', 'Medical History')}</label>
                    <p className="text-orange-600">{selectedPatient.medical_history || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('drug_allergy', 'Drug Allergy')}</label>
                    <p className="text-red-600 font-medium">{selectedPatient.drug_allergy || '-'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('emergency_contact', 'Emergency Contact')}</label>
                    <p>{selectedPatient.emergency_contact || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('emergency_phone', 'Emergency Phone')}</label>
                    <p>{selectedPatient.emergency_phone || '-'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('last_visit', 'Last Visit')}</label>
                    <p>{selectedPatient.last_visit ? formatDate(selectedPatient.last_visit) : '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">{print_lang('status', 'Status')}</label>
                    <span className={`badge ${selectedPatient.status === 'active' ? 'badge-success' : 'badge-error'}`}>
                      {print_lang(selectedPatient.status, selectedPatient.status === 'active' ? 'Active' : 'Inactive')}
                    </span>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                  <button onClick={closeModal} className="btn btn-secondary">
                    {print_lang('close', 'Close')}
                  </button>
                  <button
                    onClick={() => {
                      closeModal();
                      handleViewClinicalRecord(selectedPatient);
                    }}
                    className="btn btn-primary flex items-center gap-2 bg-green-600 hover:bg-green-700"
                  >
                    <FileText size={18} />
                    {print_lang('view_clinical_record', 'Clinical Record')}
                  </button>
                  <button onClick={handleSwitchToEdit} className="btn btn-primary flex items-center gap-2">
                    <Edit size={18} />
                    {print_lang('edit', 'Edit')}
                  </button>
                </div>
              </div>
            ) : (
              /* Add/Edit Mode */
              <form onSubmit={handleSubmit}>
                <div className="grid gap-6">
                  {/* Read Smartcard Button - only show when smartcard_enabled */}
                  {currentBranch?.smartcard_enabled === 1 && (
                    <div className="flex justify-center">
                      <button
                        type="button"
                        onClick={handleReadSmartcard}
                        disabled={isReadingCard}
                        className="btn btn-secondary flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 disabled:opacity-50"
                      >
                        <CreditCard size={20} />
                        {isReadingCard
                          ? print_lang('reading_smartcard', 'Reading Smartcard...')
                          : print_lang('read_smartcard', 'Read Smartcard')
                        }
                      </button>
                    </div>
                  )}

                  {/* Photo Upload */}
                  <div className="flex justify-center">
                    <div className="text-center">
                      <label className="block text-sm font-medium mb-2">{print_lang('photo', 'Photo')}</label>
                      <div className="flex flex-col items-center gap-3">
                        {photoPreview ? (
                          <div className="relative">
                            <img
                              src={photoPreview}
                              alt="Preview"
                              className="w-32 h-32 rounded-full object-cover border-4 border-gray-200"
                            />
                            <button
                              type="button"
                              onClick={handleRemovePhoto}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <div className="w-32 h-32 rounded-full bg-gray-100 flex items-center justify-center border-4 border-dashed border-gray-300">
                            <User size={48} className="text-gray-300" />
                          </div>
                        )}
                        <input
                          ref={photoInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoChange}
                          className="hidden"
                          id="patient-photo-upload"
                        />
                        <label
                          htmlFor="patient-photo-upload"
                          className="btn btn-secondary text-sm cursor-pointer flex items-center gap-2"
                        >
                          <Upload size={16} />
                          {print_lang('upload_photo', 'Upload Photo')}
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Expired Card Warning */}
                  {expiredCardWarning && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                      <AlertTriangle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
                      <div>
                        <p className="font-medium text-red-700">{expiredCardWarning.message}</p>
                        <p className="text-sm text-red-600">
                          {print_lang('expired_on', 'Expired on')}: {formatDate(expiredCardWarning.expireDate)}
                        </p>
                        <p className="text-sm text-red-600 mt-1">
                          {print_lang('expired_card_note', 'Please ask the patient to renew their ID card')}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* DN */}
                  <div>
                    <label className="block text-sm font-medium mb-1">{print_lang('dn', 'DN')}</label>
                    {modalMode === 'add' ? (
                      <input
                        type="text"
                        className="w-full border rounded px-3 py-2 bg-gray-100 text-gray-500"
                        value={print_lang('dn_auto_assigned', 'Assigned automatically on save')}
                        disabled
                        readOnly
                      />
                    ) : (
                      <input
                        type="text"
                        className={`w-full border rounded px-3 py-2 font-mono ${selectedPatient?.dn ? 'bg-gray-100 text-gray-500' : ''}`}
                        value={formData.dn || ''}
                        placeholder="DN-69-00001"
                        // Write-once: editable only while the patient has no DN,
                        // so a number can be carried over from paper. The API
                        // enforces this too — the disabled attribute is a
                        // courtesy, not the guarantee.
                        disabled={Boolean(selectedPatient?.dn)}
                        readOnly={Boolean(selectedPatient?.dn)}
                        onChange={(e) => setFormData({ ...formData, dn: e.target.value.toUpperCase() })}
                      />
                    )}
                  </div>

                  {/* Citizen ID */}
                  <div>
                    <label className="block text-sm font-medium mb-1">{print_lang('citizen_id', 'Citizen ID')}</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={formData.citizen_id}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                          setFormData({ ...formData, citizen_id: value });
                          // Clear errors when typing
                          if (citizenIdError) setCitizenIdError(null);
                          if (citizenIdWarning) setCitizenIdWarning(null);
                        }}
                        onBlur={handleCitizenIdBlur}
                        maxLength={13}
                        placeholder="1234567890123"
                        className={`${citizenIdError ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''} ${citizenIdWarning ? 'border-yellow-500 focus:border-yellow-500 focus:ring-yellow-200' : ''}`}
                      />
                      {isCheckingCitizenId && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                        </div>
                      )}
                    </div>

                    {/* Citizen ID Error - Duplicate */}
                    {citizenIdError && (
                      <div className="mt-2 bg-red-50 border border-red-200 rounded p-3">
                        <p className="text-sm text-red-700 font-medium flex items-center gap-2">
                          <AlertTriangle size={16} />
                          {citizenIdError.message}
                        </p>
                        {citizenIdError.patient && (
                          <p className="text-sm text-red-600 mt-1">
                            {print_lang('existing_patient', 'Existing patient')}: {citizenIdError.patient.name}
                            {citizenIdError.patient.phone && ` (${citizenIdError.patient.phone})`}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Citizen ID Warning - Invalid format */}
                    {citizenIdWarning && !citizenIdError && (
                      <p className="mt-1 text-sm text-yellow-600 flex items-center gap-1">
                        <AlertTriangle size={14} />
                        {citizenIdWarning}
                      </p>
                    )}
                  </div>

                  {/* Name fields */}
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">{print_lang('prefix', 'Prefix')}</label>
                      <select
                        value={formData.prefix}
                        onChange={(e) => setFormData({ ...formData, prefix: e.target.value })}
                      >
                        {prefixOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-3">
                      <label className="block text-sm font-medium mb-1">{print_lang('name', 'Name')} *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        placeholder="ชื่อ-นามสกุล"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{print_lang('name_en', 'Name (English)')}</label>
                    <input
                      type="text"
                      value={formData.name_en}
                      onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                      placeholder="Full Name in English"
                    />
                  </div>

                  {/* Gender, DOB, Blood Group */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">{print_lang('gender', 'Gender')}</label>
                      <select
                        value={formData.gender}
                        onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                      >
                        <option value="male">{print_lang('male', 'Male')}</option>
                        <option value="female">{print_lang('female', 'Female')}</option>
                        <option value="other">{print_lang('other', 'Other')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">{print_lang('dob', 'Date of Birth')}</label>
                      <input
                        type="date"
                        value={formData.dob}
                        onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">{print_lang('blood_group', 'Blood Group')}</label>
                      <select
                        value={formData.blood_group}
                        onChange={(e) => setFormData({ ...formData, blood_group: e.target.value })}
                      >
                        {bloodGroupOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Contact */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">{print_lang('phone', 'Phone')}</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="08x-xxx-xxxx"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">{print_lang('email', 'Email')}</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="email@example.com"
                      />
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <label className="block text-sm font-medium mb-1">{print_lang('address', 'Address')}</label>
                    <textarea
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      rows={2}
                      placeholder="ที่อยู่..."
                    />
                  </div>

                  {/* Medical Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">{print_lang('medical_history', 'Medical History')}</label>
                      <textarea
                        value={formData.medical_history}
                        onChange={(e) => setFormData({ ...formData, medical_history: e.target.value })}
                        rows={2}
                        placeholder="โรคประจำตัว..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-red-600">{print_lang('drug_allergy', 'Drug Allergy')}</label>
                      <textarea
                        value={formData.drug_allergy}
                        onChange={(e) => setFormData({ ...formData, drug_allergy: e.target.value })}
                        rows={2}
                        placeholder="แพ้ยา..."
                        className="border-red-200 focus:border-red-400"
                      />
                    </div>
                  </div>

                  {/* Emergency Contact */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">{print_lang('emergency_contact', 'Emergency Contact')}</label>
                      <input
                        type="text"
                        value={formData.emergency_contact}
                        onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                        placeholder="ชื่อผู้ติดต่อฉุกเฉิน"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">{print_lang('emergency_phone', 'Emergency Phone')}</label>
                      <input
                        type="tel"
                        value={formData.emergency_phone}
                        onChange={(e) => setFormData({ ...formData, emergency_phone: e.target.value })}
                        placeholder="08x-xxx-xxxx"
                      />
                    </div>
                  </div>

                  {/* Status (only for edit mode) */}
                  {modalMode === 'edit' && (
                    <div>
                      <label className="block text-sm font-medium mb-1">{print_lang('status', 'Status')}</label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      >
                        <option value="active">{print_lang('active', 'Active')}</option>
                        <option value="inactive">{print_lang('inactive', 'Inactive')}</option>
                      </select>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                  <button type="button" onClick={closeModal} className="btn btn-secondary">
                    {print_lang('cancel', 'Cancel')}
                  </button>
                  <button type="submit" disabled={isSaving} className="btn btn-primary flex items-center gap-2">
                    <Save size={18} />
                    {isSaving ? print_lang('saving', 'Saving...') : print_lang('save', 'Save')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Patients;
